import AddIcon from "@mui/icons-material/Add";
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AppCategory,
  ConditionExpression,
  TConfigItemType,
  TOnboardingConfig,
  TVerificationAppFieldConfigItem,
} from "../../models";
import { ConditionEditorDialog } from "./ConditionEditorDialog";
import { ConditionPreview } from "./ConditionPreview";
import { DynamicStringEditor } from "./DynamicStringEditor";
import { FieldTypeChip } from "./FieldTypeChip";
import { FieldDefaultEditor } from "./FieldDefaultEditor";
import { FieldFormCheckboxRow } from "./FieldFormCheckboxRow";
import { FieldTypeSpecificProps } from "./FieldTypeSpecificProps";
import { InputActionMenu } from "./InputActionMenu";
import {
  createSimpleCondition,
} from "./conditionHelpers";
import {
  createDynamicStringFromStatic,
  getDynamicStringDefaultText,
  isDynamicStringValue,
  parseDynamicString,
} from "./dynamicStringHelpers";
import {
  FIELD_TYPE_LABELS,
  isValidFieldName,
} from "./helpers";
import {
  getQuestionnaireContentOptions,
  isQuestionnaireContentAllowed,
  TQuestionnaireContentOption,
} from "./questionnaireContentOptions";

const FIELD_TEXTFIELD_SX = { width: "100%", maxWidth: 500 } as const;
type EditingConditionTarget = "required" | "visible" | null;
type EmptyContentOption = {
  category?: AppCategory;
  fieldType?: TConfigItemType;
  value: "";
  label: string;
};
type ContentAutocompleteOption = TQuestionnaireContentOption | EmptyContentOption;

const EMPTY_CONTENT_OPTION: EmptyContentOption = {
  value: "",
  label: "None",
};

function normalizeFieldNameInput(value: string): string | undefined {
  return value.trim() ? value : undefined;
}

function getFieldDisplayLabel(field: TVerificationAppFieldConfigItem): string | null {
  const configuredLabel = getDynamicStringDefaultText(field.label);

  if (typeof configuredLabel === "string" && configuredLabel.trim()) return configuredLabel;
  if (field.name?.trim() !== "") return field.name ?? null;
  return null;
}

export const FieldCard: FC<{
  config: TOnboardingConfig;
  field: TVerificationAppFieldConfigItem;
  fieldIndex: number;
  availableConditionFields: TVerificationAppFieldConfigItem[];
  index: number;
  stepIndex: number;
  totalFields: number;
  onChange: (updated: TVerificationAppFieldConfigItem) => void;
  onDelete: () => void;
  onMove: (direction: "up" | "down") => void;
}> = memo(({
  config,
  field,
  fieldIndex,
  availableConditionFields,
  index,
  stepIndex,
  totalFields,
  onChange,
  onDelete,
  onMove,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editingConditionTarget, setEditingConditionTarget] = useState<EditingConditionTarget>(null);

  const handleDeleteClick = useCallback(() => {
    if (window.confirm("Delete this field?")) {
      onDelete();
    }
  }, [onDelete]);

  const updateProp = useCallback(
    <K extends keyof TVerificationAppFieldConfigItem>(key: K, value: TVerificationAppFieldConfigItem[K]) => {
      onChange({ ...field, [key]: value });
    },
    [onChange, field],
  );

  const toggle = useCallback(() => setExpanded((prev) => !prev), []);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const isNamelessField = field.type === TConfigItemType.NOTE;
  const nameInvalid = !isNamelessField && !isValidFieldName(field.name ?? "");
  const displayLabel = getFieldDisplayLabel(field);
  const labelDefaultText = getDynamicStringDefaultText(field.label);
  const labelHasConfiguredText = !!labelDefaultText?.trim();
  const labelIsFromName = displayLabel === field.name && !labelHasConfiguredText;
  const dynamicLabelValue = isDynamicStringValue(field.label) ? field.label : null;
  const editableDynamicLabel = dynamicLabelValue ? parseDynamicString(dynamicLabelValue) : null;

  const dynamicSubtitleValue = isDynamicStringValue(field.subtitle) ? field.subtitle : null;
  const editableDynamicSubtitle = dynamicSubtitleValue ? parseDynamicString(dynamicSubtitleValue) : null;
  const contentOptions = useMemo(
    () => getQuestionnaireContentOptions(config.category, field.type),
    [config.category, field.type],
  );
  const contentAutocompleteOptions = useMemo<ContentAutocompleteOption[]>(
    () => [EMPTY_CONTENT_OPTION, ...contentOptions],
    [contentOptions],
  );
  const selectedContentOption = useMemo(
    () => contentOptions.find((option) => option.value === field.content) ?? null,
    [contentOptions, field.content],
  );
  const requiredConditionExists = field.required !== undefined && typeof field.required !== "boolean";
  const visibilityConditionExists = field.visible !== undefined;
  const visibilityAction = !visibilityConditionExists ? (
    <Button
      size='small'
      startIcon={<AddIcon />}
      onClick={() => setEditingConditionTarget("visible")}
      sx={{ alignSelf: "flex-start" }}
    >
      Show conditionally
    </Button>
  ) : null;
  const dialogCondition = useMemo(() => {
    if (editingConditionTarget === "required") {
      return (requiredConditionExists ? field.required as ConditionExpression : null)
        ?? createSimpleCondition(availableConditionFields);
    }

    if (editingConditionTarget === "visible") {
      return (visibilityConditionExists ? field.visible as ConditionExpression : null)
        ?? createSimpleCondition(availableConditionFields);
    }

    return null;
  }, [
    availableConditionFields,
    editingConditionTarget,
    field.required,
    field.visible,
    requiredConditionExists,
    visibilityConditionExists,
  ]);

  const clearVisibleCondition = useCallback(() => {
    const { visible: _removed, ...rest } = field;
    onChange(rest);
  }, [field, onChange]);

  useEffect(() => {
    if (!field.content) {
      return;
    }

    if (isQuestionnaireContentAllowed(config.category, field.type, field.content)) {
      return;
    }

    const { content: _removed, ...rest } = field;
    onChange(rest);
  }, [config.category, field, onChange]);

  const handleRequiredCheckboxChange = useCallback(
    (checked: boolean) => {
      updateProp("required", checked);
    },
    [updateProp],
  );

  const handleRemoveRequiredCondition = useCallback(
    () => {
      updateProp("required", false);
    },
    [updateProp],
  );

  const handleSaveCondition = useCallback(
    (nextCondition: ConditionExpression) => {
      if (editingConditionTarget === "required") {
        updateProp("required", nextCondition);
      }

      if (editingConditionTarget === "visible") {
        updateProp("visible", nextCondition);
      }

      setEditingConditionTarget(null);
    },
    [editingConditionTarget, updateProp],
  );

  const handleContentChange = useCallback(
    (_: React.SyntheticEvent, option: ContentAutocompleteOption | null) => {
      if (!option?.value) {
        const { content: _removed, ...rest } = field;
        onChange(rest);
        return;
      }

      updateProp("content", option.value);
    },
    [field, onChange, updateProp],
  );

  const convertPropToDynamic = useCallback(
    (key: "label" | "subtitle") => {
      const currentValue = field[key];
      const staticValue = typeof currentValue === "string" ? currentValue : undefined;

      updateProp(
        key,
        createDynamicStringFromStatic(
          staticValue,
          createSimpleCondition(availableConditionFields),
        ) as TVerificationAppFieldConfigItem[typeof key],
      );
    },
    [availableConditionFields, field, updateProp],
  );
  const getStringPropertyActions = useCallback(
    (key: "label" | "subtitle") => [
      {
        label: "Make dynamic",
        onClick: () => convertPropToDynamic(key),
      },
    ],
    [convertPropToDynamic],
  );

  return (
    <Box>
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        onClick={toggle}
        sx={{
          py: 0.75,
          px: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <IconButton
          aria-label={expanded ? "Collapse" : "Expand"}
          size='small'
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
          onClick={(e) => { stop(e); toggle(); }}
        >
          <ExpandMoreIcon fontSize='small' />
        </IconButton>

        {displayLabel ? (
          <Typography
            variant='body2'
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: labelIsFromName ? "monospace" : undefined,
              color: labelIsFromName ? "text.secondary" : "text.primary",
            }}
          >
            {displayLabel}
          </Typography>
        ) : (
          <Typography variant='body2' sx={{ color: "text.disabled", fontStyle: "italic" }}>
            {FIELD_TYPE_LABELS[field.type] ?? field.type} field
          </Typography>
        )}

        <FieldTypeChip type={field.type} />

        {dynamicLabelValue && (
          <Chip label='Dynamic' size='small' variant='outlined' color='info' />
        )}

        <Box sx={{ flex: 1 }} />

        <IconButton
          aria-label='Move field up'
          onClick={(e) => { stop(e); onMove("up"); }}
          disabled={index === 0}
          size='small'
        >
          <ArrowUpwardIcon fontSize='small' />
        </IconButton>
        <IconButton
          aria-label='Move field down'
          onClick={(e) => { stop(e); onMove("down"); }}
          disabled={index >= totalFields - 1}
          size='small'
        >
          <ArrowDownwardIcon fontSize='small' />
        </IconButton>
        <IconButton
          aria-label='Delete field'
          onClick={(e) => { stop(e); handleDeleteClick(); }}
          size='small'
          sx={{
            color: (theme) => alpha(theme.palette.error.main, 0.45),
            "&:hover": {
              color: "error.main",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            },
          }}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1.5} sx={{ pt: 1, pb: 2, pl: 4.5, ...FIELD_TEXTFIELD_SX }}>
          {!isNamelessField ? (
            <TextField
              label='Name'
              value={field.name ?? ""}
              onChange={(e) => updateProp("name", normalizeFieldNameInput(e.target.value))}
              size='small'
              fullWidth
              error={nameInvalid}
              helperText={
                nameInvalid
                  ? "Must be snake_case (lowercase with single underscores)"
                  : undefined
              }
            />
          ) : null}
          {dynamicLabelValue ? (
            editableDynamicLabel ? (
              <DynamicStringEditor
                availableFields={availableConditionFields}
                config={config}
                fieldIndex={fieldIndex}
                property='label'
                stepIndex={stepIndex}
                title='Label'
                value={dynamicLabelValue}
                onChange={(nextValue) => updateProp("label", (nextValue ?? "") as TVerificationAppFieldConfigItem["label"])}
              />
            ) : (
              <Chip
                label='Label: dynamic — edit in Advanced tab'
                size='small'
                variant='outlined'
                color='info'
                sx={{ alignSelf: "flex-start" }}
              />
            )
          ) : (
            <TextField
              label='Label'
              value={field.label ?? ""}
              onChange={(e) => updateProp("label", e.target.value)}
              size='small'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputActionMenu
                    actions={getStringPropertyActions("label")}
                    label='Label'
                  />
                ),
              }}
            />
          )}
          {contentOptions.length > 0 ? (
            <Autocomplete<ContentAutocompleteOption, false, false, false>
              options={contentAutocompleteOptions}
              value={selectedContentOption}
              onChange={handleContentChange}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props}>
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Content",
                  }}
                  label='Content'
                  size='small'
                />
              )}
            />
          ) : null}
          {!isNamelessField ? (
            requiredConditionExists ? (
              <ConditionPreview
                availableFields={availableConditionFields}
                condition={field.required}
                onEdit={() => setEditingConditionTarget("required")}
                onRemove={handleRemoveRequiredCondition}
                title='Required if'
              />
            ) : (
              <Stack direction='row' spacing={1.5} alignItems='center' justifyContent='space-between' flexWrap='wrap'>
                <FieldFormCheckboxRow
                  label='Required'
                  checked={field.required === true}
                  onChange={handleRequiredCheckboxChange}
                />
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => setEditingConditionTarget("required")}
                >
                  Make conditional
                </Button>
              </Stack>
            )
          ) : null}
          {visibilityConditionExists ? (
            <ConditionPreview
              availableFields={availableConditionFields}
              condition={field.visible}
              onEdit={() => setEditingConditionTarget("visible")}
              onRemove={clearVisibleCondition}
              title='Show if'
            />
          ) : null}
          {dynamicSubtitleValue ? (
            editableDynamicSubtitle ? (
              <DynamicStringEditor
                availableFields={availableConditionFields}
                config={config}
                fieldIndex={fieldIndex}
                property='subtitle'
                stepIndex={stepIndex}
                title='Subtitle'
                value={dynamicSubtitleValue}
                onChange={(nextValue) => updateProp("subtitle", nextValue as TVerificationAppFieldConfigItem["subtitle"])}
              />
            ) : (
              <Chip
                label='Subtitle: dynamic — edit in Advanced tab'
                size='small'
                variant='outlined'
                color='info'
                sx={{ alignSelf: "flex-start" }}
              />
            )
          ) : (
            <TextField
              label='Subtitle'
              value={field.subtitle ?? ""}
              onChange={(e) => updateProp("subtitle", e.target.value || undefined)}
              size='small'
              fullWidth
              multiline
              minRows={2}
              InputProps={{
                endAdornment: (
                  <InputActionMenu
                    actions={getStringPropertyActions("subtitle")}
                    label='Subtitle'
                  />
                ),
              }}
            />
          )}
          {isNamelessField ? (
            visibilityAction
          ) : (
            <>
              <FieldDefaultEditor
                extraAction={visibilityAction}
                field={field}
                onChange={onChange}
              />
              {!visibilityConditionExists && field.default !== undefined ? visibilityAction : null}
              <FieldTypeSpecificProps field={field} onChange={onChange} />
            </>
          )}
        </Stack>
      </Collapse>
      {editingConditionTarget && dialogCondition ? (
        <ConditionEditorDialog
          availableFields={availableConditionFields}
          condition={dialogCondition}
          fieldIndex={fieldIndex}
          open
          stepIndex={stepIndex}
          title={editingConditionTarget === "required" ? "Edit required condition" : "Edit visibility condition"}
          validationConfig={config}
          validationPath={`/elements/${stepIndex}/fields/${fieldIndex}/${editingConditionTarget}`}
          onClose={() => setEditingConditionTarget(null)}
          onSave={handleSaveCondition}
        />
      ) : null}
    </Box>
  );
});
